import React, { useState, useCallback, useRef, useEffect } from "react";
import {
    Avatar,
    ChannelDate,
    Dropdown,
    DropdownItem,
    InfoButton,
    LastMessage,
    NoResults,
    PageWrapper,
    SearchIcon,
    SearchInput,
    SearchInputRow,
    SearchWrapper,
    Spinner,
    SpinnerOverlay,
    UserInfo,
    UserMeta,
    UserName,
    UserSubtitle,
    UserTag,
} from "./index.styled";
import { useSupportPage } from "./useSupportPage";
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import { SendbirdUser } from "../chat";

const getLastMessagePreview = (channel: GroupChannel): string | null => {
    const msg = channel.lastMessage;
    if (!msg) return null;
    if (msg.messageType === 'user') return (msg as any).message || null;
    if (msg.messageType === 'file') return 'Sent a file';
    return null;
};

const formatChannelDate = (createdAt: number): string =>
    new Date(createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

export interface SearchUsersResponse {
    users: SendbirdUser[];
    page: number;
}

export interface SearchUsersParams {
    query: string;
    page?: number;
}

const AVATAR_COLORS = [
    '#2e2a28', '#3b5bdb', '#2f9e44', '#e67700',
    '#c92a2a', '#5c7cfa', '#0c8599', '#862e9c',
];

const getAvatarColor = (id: number): string =>
    AVATAR_COLORS[id % AVATAR_COLORS.length];

const getInitials = (firstName: string, lastName: string): string =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const FLAG_LABELS: Record<string, string> = {
    vip:            'VIP Coaching',
    vip_elite:      'VIP Elite',
    vip_experience: 'Golf School Attendee',
};

const SVGSearch = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.7" />
        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
);

const SVGClear = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
);

export interface SupportPageProps {
    user: SendbirdUser | null;
    onChannelCreated: (channelUrl: string) => void;
}

const SupportPage: React.FC<SupportPageProps> = ({ user, onChannelCreated }) => {
    const {
        query,
        isOpen,
        users,
        isLoading,
        error,
        channelMap,
        wrapperRef,
        inputRef,
        handleInputChange,
        handleFocus,
        handleBlur,
        handleSelect,
        clearQuery,
        claimUserToSupport,
    } = useSupportPage(user);

    const [isClaiming, setIsClaiming] = useState(false);
    const [claimError, setClaimError] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const claimErrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setActiveIndex(-1);
        itemRefs.current = [];
    }, [users, isOpen]);

    const clearClaimError = useCallback(() => {
        if (claimErrorTimer.current) clearTimeout(claimErrorTimer.current);
        setClaimError(null);
    }, []);

    const handleClaim = useCallback(async (selectedUser: SendbirdUser) => {
        if (!user) return;
        handleSelect(selectedUser);
        setIsClaiming(true);
        clearClaimError();
        try {
            const supportId = user.id || window.pgUser.ID;
            if (!supportId) {
                throw new Error('Support user ID not found.');
            }
            const channel = await claimUserToSupport({ ...user, id: supportId }, selectedUser);
            onChannelCreated(channel.url);
        } catch {
            setClaimError('Failed to create channel. Please try again.');
            claimErrorTimer.current = setTimeout(() => setClaimError(null), 3500);
        } finally {
            setIsClaiming(false);
        }
    }, [user, claimUserToSupport, handleSelect, onChannelCreated, clearClaimError]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || users.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => {
                const next = prev < users.length - 1 ? prev + 1 : 0;
                itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
                return next;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => {
                const next = prev > 0 ? prev - 1 : users.length - 1;
                itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
                return next;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < users.length) {
                handleClaim(users[activeIndex]);
            }
        }
    }, [isOpen, users, activeIndex, handleClaim]);

    return (
        <PageWrapper>
            {isClaiming && (
                <SpinnerOverlay>
                    <Spinner />
                </SpinnerOverlay>
            )}
            <SearchWrapper ref={wrapperRef} onBlur={handleBlur}>
                <SearchInputRow>
                    <SearchIcon>
                        <SVGSearch />
                    </SearchIcon>
                    <SearchInput
                        ref={inputRef}
                        type="text"
                        placeholder="Search members by email..."
                        value={query}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { clearClaimError(); handleInputChange(e); }}
                        onFocus={() => { clearClaimError(); handleFocus(); }}
                        onKeyDown={handleKeyDown}
                        aria-label="Search users by email"
                        autoComplete="off"
                    />
                    {query ? (
                        <InfoButton type="button" aria-label="Clear search" tabIndex={0} onMouseDown={(e: React.MouseEvent<HTMLElement>) => { e.preventDefault(); clearQuery(); }}>
                            <SVGClear />
                        </InfoButton>
                    ) : (
                        <InfoButton type="button" aria-label="Search info" tabIndex={0} as="span">
                            <SVGSearch />
                        </InfoButton>
                    )}
                </SearchInputRow>

                {isOpen && (
                    <Dropdown>
                        {isLoading && (
                            <NoResults>Searching...</NoResults>
                        )}
                        {!isLoading && error && (
                            <NoResults>{error}</NoResults>
                        )}
                        {isClaiming && (
                            <NoResults>Joining channel...</NoResults>
                        )}
                        {!isClaiming && claimError && (
                            <NoResults>{claimError}</NoResults>
                        )}
                        {!isLoading && !error && !isClaiming && !claimError && users.length > 0 && (
                            users.map((user: SendbirdUser, idx: number) => (
                                <DropdownItem
                                    key={user.id}
                                    ref={(el: HTMLDivElement | null) => { itemRefs.current[idx] = el; }}
                                    onMouseDown={() => handleClaim(user)}
                                    role="option"
                                    aria-selected={activeIndex === idx}
                                    $active={activeIndex === idx}
                                >
                                    <Avatar $bg={getAvatarColor(user.id ?? 0)}>
                                        {getInitials(user.first_name ?? '', user.last_name ?? '')}
                                    </Avatar>
                                    <UserInfo>
                                        <UserMeta>
                                            <UserName>
                                                {user.first_name} {user.last_name}
                                            </UserName>
                                            {user.userFlags?.map(flag => (
                                                <UserTag key={flag}>{FLAG_LABELS[flag] ?? flag}</UserTag>
                                            ))}
                                        </UserMeta>
                                        <UserSubtitle>{user.email}</UserSubtitle>
                                    </UserInfo>
                                </DropdownItem>
                            ))
                        )}
                        {!isLoading && !error && !isClaiming && !claimError && users.length === 0 && query.trim().length >= 4 && (
                            <NoResults>No users found for &ldquo;{query}&rdquo;</NoResults>
                        )}
                        {!isLoading && !error && !isClaiming && !claimError && query.trim().length < 4 && (
                            <NoResults>Type at least 4 characters to search</NoResults>
                        )}
                    </Dropdown>
                )}
            </SearchWrapper>
        </PageWrapper>
    );
};

export default SupportPage;
